import { useBambooTopUpTransactionMutation } from '@/gql/generated';
import { formatDateToTimezone } from '@/lib/timezone';
import { calculateDiscountPercent } from '@/lib/utils';
import { AuthenticatedContext } from '@/pages/(gapsdk)/(authenticated)/_provider/authenticated-provider';
import {
  ActionChargeMagtek,
  StepChargeMagTek,
  TCardData,
  TInputCardInformation,
  TPaymentFailed,
} from '@/types/payment/charge.type';
import { paymentMethods } from '@/utils/constant';
import bigDecimal from 'js-big-decimal';
import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';
import { useContext, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PaymentFailed from './_components/payment-failed';
import PaymentProcessing from './_components/payment-processing';
import PaymentRemoveCardFailed from './_components/payment-remove-card-failed';
import PaymentRemoveCardSuccessful from './_components/payment-remove-card-successful';
import PaymentSuccess from './_components/payment-successful';
import PresentCard from './_components/present-card';

export default function ChargeAddFundPage() {
  const navigate = useNavigate();
  const { code } = useParams();
  const { user } = useContext(AuthenticatedContext);
  const [searchParams] = useSearchParams();

  const price = searchParams.get('price') ?? '0';
  const balanceChange = searchParams.get('balance_change') ?? '0';
  const id = searchParams.get('id');
  const discountPercentValue = calculateDiscountPercent(
    Number(balanceChange),
    Number(price),
  );

  const [error, setError] = useState('');
  const [cardTypeSubmit, setCardTypeSubmit] = useState<string>('');

  const [step, setStep] = useState<StepChargeMagTek>(
    StepChargeMagTek.PRESENT_CARD,
  );

  const bambooTopUpTransactionMutation = useBambooTopUpTransactionMutation({
    onError: () => {
      setError('Something went wrong');
      setStep(StepChargeMagTek.PAYMENT_FAILED);
    },
    onMutate: () => {
      setStep(StepChargeMagTek.PAYMENT_PROCESSING);
    },
  });

  const onCheckCardFailed = (data: TPaymentFailed) => {
    if (data.type === 'timeout') {
      setStep(StepChargeMagTek.PAYMENT_FAILED);
      return;
    }
    if (data.type === 'canceled') {
      setStep(StepChargeMagTek.PAYMENT_FAILED);
      return;
    }
    if (data.type === 'no_data') {
      setStep(StepChargeMagTek.PAYMENT_FAILED);
    }
  };

  const onSubmitPayment = (data: TCardData) => {
    const cardInfo: TInputCardInformation = {
      card_expiration_date: data.card_exp_date,
      card_holder_name: data.card_holder_name,
      card_type: data.card_type,
    };

    // Request API submit payment
    bambooTopUpTransactionMutation.mutate(
      {
        input_obj: {
          balance_change: Number(balanceChange) ?? 0,
          date: formatDateToTimezone(
            new Date().toISOString(),
            user?.time_zone!,
            `yyyy-MM-dd'T'HH:mm:ssXXX`,
          ),
          description: '',
          discount_percent_value: Number(
            bigDecimal.multiply(discountPercentValue, 100),
          ),
          gift_card_id: id ?? '',
          whmcs_id: user?.whmcs_id ?? 0,
          payments: [
            {
              payment_method: paymentMethods.CHARGE,
              price: price,
              card_info: {
                ...omitBy(cardInfo, isNil),
              },
              charge_info: {
                action: data.action,
                arqc: data.arqc,
                data_dfdf56: data.dfdf56,
                data_dfdf57: data.dfdf57,
                device_sn: data.device_serial_number,
              },
            },
          ],
        },
      },
      {
        onSuccess: (response) => {
          setCardTypeSubmit(response.bamboo_top_up_transaction.card_type ?? '');
          if (
            data.action === ActionChargeMagtek.TAP ||
            data.action === ActionChargeMagtek.SWIPE
          ) {
            setStep(StepChargeMagTek.PAYMENT_SUCCESS);
            return;
          }

          if (data.action === ActionChargeMagtek.INSERT) {
            setStep(StepChargeMagTek.REMOVE_CARD_SUCCESSFUL);
          }
        },
      },
    );
  };

  return (
    <div className="relative size-full">
      {step === StepChargeMagTek.PRESENT_CARD && (
        <PresentCard
          onSubmitPayment={onSubmitPayment}
          onCheckCardFailed={onCheckCardFailed}
        />
      )}
      {step === StepChargeMagTek.PAYMENT_PROCESSING && <PaymentProcessing />}
      {step === StepChargeMagTek.PAYMENT_FAILED && (
        <PaymentFailed
          errors={error}
          onBack={() => navigate(`/gift-card/add-fund/${code}?id=${id}`)}
        />
      )}
      {step === StepChargeMagTek.REMOVE_CARD_SUCCESSFUL && (
        <PaymentRemoveCardSuccessful
          onBack={() => setStep(StepChargeMagTek.PAYMENT_SUCCESS)}
        />
      )}
      {step === StepChargeMagTek.REMOVE_CARD_FAILED && (
        <PaymentRemoveCardFailed
          onBack={() => setStep(StepChargeMagTek.PAYMENT_FAILED)}
        />
      )}
      {step === StepChargeMagTek.PAYMENT_SUCCESS && (
        <PaymentSuccess
          onBack={() => {
            navigate(
              `/gift-card/add-fund/${code}/signature?transaction_id=${bambooTopUpTransactionMutation.data?.bamboo_top_up_transaction.id}&price=${price}`,
            );
          }}
          cardType={cardTypeSubmit}
        />
      )}
    </div>
  );
}
